// Compatibility shim for WorkOS AuthKit
// This file provides backward compatibility for components importing from AuthContext
//
// MIGRATION NOTE (January 2026):
// Migrated to WorkOS AuthKit for authentication.
// Onboarding status now managed via Convex users table.

/* eslint-disable react-refresh/only-export-components */
import { useAuth as useWorkOSAuth } from '@workos-inc/authkit-react';
import { useQuery } from 'convex/react';
import { api } from '@careercard/convex/convex/_generated/api';
import { createContext, useMemo } from 'react';

// AuthUser type compatible with the original interface
export interface AuthUser {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  emailVerified?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  // Additional fields for compatibility
  displayName?: string | null;
  profileImageUrl?: string | null;
  role?: string;
  user_metadata?: Record<string, unknown>;
}

// Auth state interface
interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
}

// Create context for compatibility
const AuthContext = createContext<AuthState | undefined>(undefined);

// Hook that wraps WorkOS auth and provides compatible interface
export function useAuth() {
  const workosAuth = useWorkOSAuth();

  // Get the Convex user data for onboardingComplete status
  // This provides real-time sync of the onboarding status
  const convexUser = useQuery(api.users.get);

  const authState = useMemo((): AuthState => {
    const workosUser = workosAuth.user;

    // Map WorkOS user to AuthUser format
    const displayName = workosUser?.firstName && workosUser?.lastName
      ? `${workosUser.firstName} ${workosUser.lastName}`.trim()
      : workosUser?.firstName || workosUser?.lastName || null;

    const user: AuthUser | null = workosUser ? {
      id: workosUser.id,
      email: workosUser.email || '',
      name: displayName,
      image: workosUser.profilePictureUrl || null,
      emailVerified: workosUser.emailVerified ?? undefined,
      createdAt: workosUser.createdAt ? new Date(workosUser.createdAt) : undefined,
      updatedAt: workosUser.updatedAt ? new Date(workosUser.updatedAt) : undefined,
      // Additional compatibility fields
      displayName,
      profileImageUrl: workosUser.profilePictureUrl || null,
      role: 'user', // Default role, WorkOS doesn't have roles in the basic user object
      user_metadata: {},
    } : null;

    return {
      user,
      isLoading: workosAuth.isLoading,
      isAuthenticated: !!workosUser,
      error: null,
    };
  }, [workosAuth.user, workosAuth.isLoading]);

  // Onboarding status from Convex user data
  // Default to true while loading or if not set (for existing users who completed onboarding before migration)
  // Only show the onboarding banner if explicitly set to false
  const onboardingComplete = convexUser === undefined ? true : (convexUser?.onboardingComplete ?? true);

  return {
    ...authState,
    // Alias for compatibility - some components use `loading` instead of `isLoading`
    loading: authState.isLoading,
    signIn: workosAuth.signIn,
    signUp: workosAuth.signUp,
    signOut: workosAuth.signOut,
    getAccessToken: workosAuth.getAccessToken,
    // Onboarding status from Convex (real-time sync)
    onboardingComplete,
    // updateOnboardingStatus is deprecated - use the completeOnboarding mutation directly
    updateOnboardingStatus: async (_status: boolean) => {
      console.warn('updateOnboardingStatus is deprecated. Use useMutation(api.users.completeOnboarding) directly.');
    },
  };
}

// Alias for components that use useAuthState
export function useAuthState() {
  return useAuth();
}

// Re-export AuthKitProvider as AuthProvider for compatibility
export { AuthKitProvider as AuthProvider } from '@workos-inc/authkit-react';

// Export the context for direct imports
export default AuthContext;
/* eslint-enable react-refresh/only-export-components */